/**
 * The ONE index rule behind every roving-tabindex radiogroup in the app.
 *
 * *** WHY IT IS A MODULE. *** `role="radio"` PROMISES arrow keys — a group of
 * buttons carrying that role declares an interaction model (arrows move AND
 * select, Home/End jump to the ends, Tab exits the group) that the browser does
 * not implement for you. The customer-facing decline sheet
 * (`EstimateRespondSheetFields.js`) implemented it once; the admin decline modal
 * now needs the same contract for the same four reasons. Two hand-written
 * copies of a keyboard contract is exactly the shape this codebase keeps
 * getting bitten by — so the index math lives here, tested, and both surfaces
 * call it. The two groups deliberately keep their OWN styling (customer page
 * and admin modal are different design languages); only the behaviour is
 * shared.
 *
 * `null` means "not a navigation key" — the caller must NOT preventDefault, or
 * it swallows Tab, Escape and typing.
 */

/**
 * @param {string} key            KeyboardEvent.key
 * @param {number} currentIndex   index of the selected option, or -1 for none
 * @param {number} length         number of options
 * @returns {number|null}         index to move to, or null if `key` is not ours
 */
export function nextRadioIndex(key, currentIndex, length) {
  if (!Number.isInteger(length) || length <= 0) return null;
  const forward = key === 'ArrowDown' || key === 'ArrowRight';
  const back = key === 'ArrowUp' || key === 'ArrowLeft';
  const first = key === 'Home';
  const last = key === 'End';
  if (!forward && !back && !first && !last) return null;
  // Home/End are part of the radiogroup contract APG describes, and a group
  // that answers arrows but not Home/End teaches the pattern only halfway.
  if (first) return 0;
  if (last) return length - 1;
  const current = Number.isInteger(currentIndex) ? currentIndex : -1;
  // NOTHING SELECTED YET is the case that decides where an arrow lands, and it
  // is the one a naive `(current + step + len) % len` gets wrong: from -1, a
  // forward step must reach the FIRST row (not the second) and a backward step
  // the LAST. Biasing `from` rather than special-casing the result keeps the
  // wrap arithmetic in one expression.
  const from = current === -1 ? (forward ? -1 : 0) : current;
  return (from + (forward ? 1 : -1) + length) % length;
}

export default nextRadioIndex;
